import React, { useState, useEffect, useCallback } from 'react';
import MobileFilterCollapse from '../../components/common/form/MobileFilterCollapse';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Paper, Typography, Chip, TextField, MenuItem,
  Grid, Alert, Divider, InputAdornment, IconButton, Stack,
} from '@mui/material';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import { downloadShipmentStatement } from '../../utils/shipmentStatement';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FactoryIcon from '@mui/icons-material/Factory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ReplayIcon from '@mui/icons-material/Replay';
import ScienceIcon from '@mui/icons-material/Science';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ShipmentMgmtCreateDialog from './ShipmentMgmtCreateDialog';
import ShipmentMgmtDetailDialog from './ShipmentMgmtDetailDialog';
import ShipmentStatementDialog from './ShipmentStatementDialog';
import * as shipmentMgmtApi from '../../lib/api/shipmentMgmt';

const SOURCE_ICON = {
  PRODUCTION: <FactoryIcon fontSize="small" />,
  WAREHOUSE: <WarehouseIcon fontSize="small" />,
  RETURN: <ReplayIcon fontSize="small" />,
  SAMPLE: <ScienceIcon fontSize="small" />,
};

// 화면에 쓰는 이름만 바꾼다. 코드값(PRODUCTION/WAREHOUSE)은 그대로다 —
// 코드값까지 바꾸면 쌓인 데이터를 전부 손봐야 한다.
const SOURCE_LABEL = {
  PRODUCTION: '납품',
  WAREHOUSE: '창고',
  RETURN: '반품',
  SAMPLE: '샘플',
};

const SOURCE_COLOR = {
  PRODUCTION: 'primary',
  WAREHOUSE: 'secondary',
  RETURN: 'warning',
  SAMPLE: 'info',
};

const STATUS_COLOR = {
  PENDING: 'default',
  APPROVED: 'info',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const hasAuth = (user, authTyp) =>
  (user?.privilegeList || []).some(auth => auth?.authTyp === authTyp && auth?.authYn === 'Y');

const ShipmentMgmtListContainer = () => {
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));
  const canProcessShipment = hasAuth(user, 'MANU_SHIPMENT_PROCESS');
  const navigate = useNavigate();

  // 알림에서 넘어온 "이 건을 열어라" 지시. 목록 검색 조건(filters)과는 성격이 달라
  // 별도로 다룬다.
  const [searchParams, setSearchParams] = useSearchParams();

  // 데이터
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 요약
  const [summary, setSummary] = useState(null);

  // 필터
  const [filters, setFilters] = useState({
    sourceType: '',
    status: '',
    keyword: '',
    startDate: '',
    endDate: '',
  });

  // 페이징
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // 다이얼로그
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // 거래명세표: 서버에서 받아온 데이터를 들고 있다가 입력을 받고 파일을 만든다.
  const [statementTarget, setStatementTarget] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  // 묶어서 명세표를 뽑을 출고들. 고객사가 다르면 못 묶는다.
  const [selectedIds, setSelectedIds] = useState([]);

  // 데이터 로드
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: paginationModel.page + 1,
        size: paginationModel.pageSize,
      };
      if (filters.sourceType) params.sourceType = filters.sourceType;
      if (filters.status) params.status = filters.status;
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const res = await shipmentMgmtApi.getShipmentList(params);
      if (res.data?.success) {
        const data = res.data.data;
        setRows((data.content || []).map(item => ({ ...item, id: item.shipmentId })));
        setTotalCount(data.totalElements || 0);
      }
    } catch (err) {
      console.error('출고 목록 조회 에러:', err);
      setError('출고 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const res = await shipmentMgmtApi.getShipmentSummary(params);
      if (res.data?.success) {
        setSummary(res.data.data);
      }
    } catch {
      // 무시
    }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // 알림 클릭으로 들어온 shipmentId 를 반영한다. 이미 이 화면에 있는 상태에서 다른
  // 알림을 눌러도(경로는 그대로고 쿼리 파라미터만 바뀐다) 다시 반응하도록 의존성에
  // shipmentId 자체를 넣는다 — navigate 만으로는 같은 경로라 재조회가 안 된다.
  const shipmentIdParam = searchParams.get('shipmentId');
  useEffect(() => {
    if (shipmentIdParam) {
      setSelectedShipmentId(shipmentIdParam);
    }
  }, [shipmentIdParam]);

  // candidateId 는 현재 프론트에 대응하는 상세 다이얼로그가 없어 열지 않는다.
  // (출하후보 상세 화면이 생기면 여기서 같은 방식으로 처리한다.)

  // 출고 등록
  const handleCreate = async (formData) => {
    if (!canProcessShipment) {
      setSaveError('출고 처리 권한이 없습니다.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await shipmentMgmtApi.createShipment(formData);
      if (res.data?.success) {
        setOpenCreate(false);
        fetchData();
        fetchSummary();
      } else {
        setSaveError(res.data?.message || '등록에 실패했습니다.');
      }
    } catch (err) {
      setSaveError(err?.response?.data?.message || '등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = () => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({ sourceType: '', status: '', keyword: '', startDate: '', endDate: '' });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // 컬럼 정의
  /**
   * 거래명세표 출력 - 1단계. 서버에서 명세표 데이터를 받아 다이얼로그를 연다.
   *
   * 양식에서 비는 칸(대표자명·인수자·비고·메모)은 시스템에 둘 자리가 없어 화면에서 받는다.
   * 그 입력은 다이얼로그가 한 번에 받는다.
   */
  const handleStatement = async (row) => {
    setError(null);
    try {
      const res = await shipmentMgmtApi.getShipmentStatement(row.shipmentId);
      const data = res?.data?.data;
      if (!data) {
        setError('명세표 데이터를 불러오지 못했습니다.');
        return;
      }
      setStatementTarget({ shipmentId: row.shipmentId, data });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      setError(message || '명세표 데이터를 불러오지 못했습니다.');
    }
  };

  /**
   * 여러 출고를 한 장으로 묶어 명세표를 뽑는다.
   *
   * 고객사가 다르면 만들지 않는다. 다른 회사 물건이 한 장에 섞이면 그건 명세표가 아니다.
   * 화면에서 먼저 걸러 주고, 서버도 다시 본다.
   */
  const handleBundledStatement = async () => {
    setError(null);
    const picked = rows.filter((r) => selectedIds.includes(r.shipmentId));
    // 필드명은 customerNm 이다. customerName 으로 읽고 있어서 모든 행이 '(고객사 미지정)' 으로
    // 뭉쳐졌고, 고객사가 달라도 경고가 안 떴다.
    const customers = [...new Set(picked.map((r) => r.customerNm || '(고객사 미지정)'))];
    if (customers.length > 1) {
      setError(`고객사가 다른 출고는 한 장에 묶을 수 없습니다. (${customers.join(', ')})`);
      return;
    }

    try {
      const res = await shipmentMgmtApi.getBundledStatement(selectedIds);
      const data = res?.data?.data;
      if (!data) {
        setError(res?.data?.message || '명세표 데이터를 불러오지 못했습니다.');
        return;
      }
      // 입력 받는 화면은 한 건일 때와 같다. 뽑고 나서 묶인 전부에 발급 표시를 남긴다.
      setStatementTarget({ shipmentId: selectedIds, data });
    } catch (err) {
      setError(err?.response?.data?.message || '명세표 데이터를 불러오지 못했습니다.');
    }
  };

  /** 다이얼로그에서 받은 값으로 실제 파일을 만든다. */
  const handleStatementSubmit = async (extra) => {
    if (!statementTarget) return;
    setStatementLoading(true);
    setError(null);
    try {
      await downloadShipmentStatement(statementTarget.data, extra);

      // 내려받은 뒤에만 발급으로 기록한다. 조회만 하고 만 것을 발급으로 남기면 안 된다.
      // 묶어 뽑았으면 묶인 전부에 남긴다 — 한 장이 그 건들을 대신한다.
      const ids = Array.isArray(statementTarget.shipmentId)
        ? statementTarget.shipmentId
        : [statementTarget.shipmentId];
      await Promise.all(ids.map((id) => shipmentMgmtApi.markStatementIssued(id)));
      setSelectedIds([]);
      setStatementTarget(null);
      fetchData();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      setError(message || '거래명세표 출력에 실패했습니다.');
    } finally {
      setStatementLoading(false);
    }
  };

  const columns = [
    {
      field: 'shipmentNumber',
      headerName: '출고번호',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          color="primary"
          sx={{ cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: '#1565c0' } }}
          onClick={(e) => { e.stopPropagation(); setSelectedShipmentId(params.row.shipmentId); }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'sourceType',
      headerName: '유형',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          icon={SOURCE_ICON[params.value] || SOURCE_ICON.PRODUCTION}
          label={SOURCE_LABEL[params.value] || '납품'}
          size="small"
          variant="outlined"
          color={SOURCE_COLOR[params.value] || 'primary'}
        />
      ),
    },
    {
      field: 'productName',
      headerName: '제품명',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || params.row.stockProductName || '-'}
        </Typography>
      ),
    },
    {
      field: 'customerNm',
      headerName: '고객사',
      width: 130,
      // 창고 입고 건은 고객사가 없다. 빈칸으로 두면 데이터가 빠진 것처럼 보인다.
      renderCell: (params) => (params.value
        ? <Typography variant="body2">{params.value}</Typography>
        : <Typography variant="body2" color="text.disabled">—</Typography>),
    },
    {
      field: 'shipmentDate',
      headerName: '출고일',
      width: 110,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'quantity',
      headerName: '수량',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: '상태',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.row.statusName || params.value}
          size="small"
          color={STATUS_COLOR[params.value] || 'default'}
        />
      ),
    },
    {
      field: 'statementIssuedYn',
      headerName: '명세표',
      width: 170,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          <Chip
            label={params.value === 'Y' ? '발급' : params.row.statementRequiredYn === 'Y' ? '필수' : '미발급'}
            size="small"
            color={params.value === 'Y' ? 'success' : params.row.statementRequiredYn === 'Y' ? 'warning' : 'default'}
            variant={params.value === 'Y' ? 'filled' : 'outlined'}
          />
          {/* 이미 발급한 건도 다시 뽑을 수 있어야 한다. 종이는 잃어버리기 마련이다. */}
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); handleStatement(params.row); }}
          >
            {params.value === 'Y' ? '재출력' : '출력'}
          </Button>
        </Stack>
      ),
    },
    {
      field: 'createdByName',
      headerName: '등록자',
      width: 90,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'createdAt',
      headerName: '등록일',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value?.substring(0, 10),
    },
  ];

  return (
    <Box>
      {/* 요약 카드 */}
      {summary && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">출고 현황</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">전체</Typography>
              <Typography variant="h6" fontWeight="bold">{summary.totalCount || 0}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">대기</Typography>
              <Typography variant="h6" color="text.secondary">{summary.pendingCount || 0}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">승인</Typography>
              <Typography variant="h6" color="info.main">{summary.approvedCount || 0}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">완료</Typography>
              <Typography variant="h6" color="success.main">{summary.completedCount || 0}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">총 출고 수량</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {(summary.totalShippedQty || 0).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* 필터 */}
      <MobileFilterCollapse summary={[filters.startDate && `${filters.startDate} ~ ${filters.endDate || ''}`, filters.keyword && `"${filters.keyword}"`].filter(Boolean).join(' · ')}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="출고 유형"
              size="small"
              fullWidth
              value={filters.sourceType}
              onChange={(e) => setFilters(prev => ({ ...prev, sourceType: e.target.value }))}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="PRODUCTION">납품</MenuItem>
              <MenuItem value="WAREHOUSE">창고</MenuItem>
              <MenuItem value="RETURN">반품 출고</MenuItem>
              <MenuItem value="SAMPLE">샘플 출고</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="상태"
              size="small"
              fullWidth
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="PENDING">대기</MenuItem>
              <MenuItem value="APPROVED">승인</MenuItem>
              <MenuItem value="COMPLETED">완료</MenuItem>
              <MenuItem value="CANCELLED">취소</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="시작일"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="종료일"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="검색"
              size="small"
              fullWidth
              placeholder="출고번호, 제품명, 고객사..."
              value={filters.keyword}
              onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button size="small" onClick={handleClearFilters} startIcon={<ClearIcon />}>
              초기화
            </Button>
          </Grid>
        </Grid>
      </Paper>
      </MobileFilterCollapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* 목록 */}
      <Paper elevation={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="bold">출고 목록</Typography>
            <Chip label={`${totalCount}건`} size="small" />
            {selectedIds.length > 0 && (
              <Chip label={`${selectedIds.length}건 선택`} size="small" color="primary" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* 여러 건을 한 장으로. 고객사가 다르면 누르기 전에 알려준다. */}
            {selectedIds.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ReceiptLongIcon />}
                onClick={handleBundledStatement}
              >
                묶어서 명세표
              </Button>
            )}
            {/* 완품검사 합격 후 대기 중인 출하후보 목록으로. 아직 출고로 등록되지 않은
                건들이 여기 모인다 — DB/API 에만 있던 것을 볼 수 있게 한 화면이다. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PendingActionsIcon />}
              onClick={() => navigate('/shipmentmng/candidates')}
            >
              출하 대기 목록
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setSaveError(null); setOpenCreate(true); }}
              disabled={!canProcessShipment}
            >
              출고 등록
            </Button>
          </Box>
        </Box>
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={totalCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          autoHeight
          density="compact"
          checkboxSelection
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={setSelectedIds}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
          }}
        />
      </Paper>

      {/* 등록 다이얼로그 */}
      <ShipmentMgmtCreateDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSave={handleCreate}
        saving={saving}
        saveError={saveError}
      />

      {/* 상세 다이얼로그 */}
      <ShipmentMgmtDetailDialog
        shipmentId={selectedShipmentId}
        onClose={() => {
          setSelectedShipmentId(null);
          // URL 에 shipmentId 가 남아있으면, 닫은 뒤 같은 알림을 다시 눌러도
          // 파라미터가 안 바뀐 것으로 보여 다이얼로그가 안 열린다. 히스토리를
          // 더럽히지 않게 replace 로 지운다.
          if (searchParams.has('shipmentId')) {
            const next = new URLSearchParams(searchParams);
            next.delete('shipmentId');
            setSearchParams(next, { replace: true });
          }
        }}
        onRefresh={() => { fetchData(); fetchSummary(); }}
        canProcessShipment={canProcessShipment}
      />

      {/* 거래명세표 출력 */}
      <ShipmentStatementDialog
        open={Boolean(statementTarget)}
        data={statementTarget?.data}
        loading={statementLoading}
        onClose={() => setStatementTarget(null)}
        onSubmit={handleStatementSubmit}
      />
    </Box>
  );
};

export default ShipmentMgmtListContainer;
